import React, { useState } from 'react';
import axios from 'axios';
import { Admin_Employee } from '../../../../constants';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
const roles = [
  { id: 1, name: 'Agent' },
  { id: 2, name: 'Business development manager' },
  { id: 3, name: 'Zonal Head Parivartan' },
];

const AddEmployeeModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    EmployeeId: '',
    EmployeePhone: '',
    EmployeeName: '',
    EmployeeRoleID: '',
    EmployeeMailId: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      const res = await axios.post(`${Admin_Employee}`, {
        EmployeeId: Number(form.EmployeeId),
        EmployeePhone: form.EmployeePhone,
        EmployeeName: form.EmployeeName,
        EmployeeRoleID: Number(form.EmployeeRoleID),
        EmployeeMailId: form.EmployeeMailId,
      });
      if (res.data.success) {
        setSuccessMsg(res.data.message || 'Employee created successfully');
        setTimeout(() => {
          onClose();
          setSuccessMsg('');
        }, 1000);
      } else {
        setError(res.data.message || 'Failed to create employee');
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to create employee'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add New Employee
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            name="EmployeeId"
            type="number"
            label="Employee ID"
            value={form.EmployeeId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={loading}
          />
          <TextField
            name="EmployeeName"
            type="text"
            label="Employee Name"
            value={form.EmployeeName}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={loading}
          />
          <TextField
            name="EmployeePhone"
            type="number"
            label="Phone"
            value={form.EmployeePhone}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={loading}
          />
          <FormControl fullWidth margin="normal" required disabled={loading}>
            <InputLabel>Role</InputLabel>
            <Select
              name="EmployeeRoleID"
              value={form.EmployeeRoleID}
              label="Role"
              onChange={handleChange}
            >
              <MenuItem value="">
                <em>Select Role</em>
              </MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            name="EmployeeMailId"
            type="email"
            label="Email"
            value={form.EmployeeMailId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={loading}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {successMsg && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {successMsg}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const EmployeeFilters = ({
  search,
  setSearch,
  filterActive,
  setFilterActive,
  filterRole,
  setFilterRole,
  setPage,
  availableRoles,
  resetFilters,
  totalEmployees,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  return (
    <>
       {/* Header */}
      <Box mb={4}>
        <Typography variant="h5" fontWeight="bold">
          Employee Management
        </Typography>
        <Typography color="text.secondary" mt={1}>
          {totalEmployees} employees found
        </Typography>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ mb: 4, p: 3 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          {/* Filter Inputs */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {/* Search */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <TextField
                    label="Search by name or ID"
                    value={search}
                    onChange={(e) => {
                      setPage(1);
                      setSearch(e.target.value);
                    }}
                    size="small"
                  />
                </FormControl>
              </Grid>
              {/* Status Filter */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterActive}
                    label="Status"
                    onChange={(e) => {
                      setPage(1);
                      setFilterActive(e.target.value);
                    }}
                  >
                    <MenuItem value="All">All Statuses</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Role Filter */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={filterRole}
                    label="Role"
                    onChange={(e) => {
                      setPage(1);
                      setFilterRole(e.target.value);
                    }}
                  >
                    <MenuItem value="All">All Roles</MenuItem>
                    {availableRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          {/* Action Buttons */}
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="outlined"
              size="small"
              onClick={resetFilters}
              sx={{ mr: 1 }}
            >
              Reset Filters
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setIsAddModalOpen(true)}
            >
              Add Employee
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <AddEmployeeModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </>
  );
};
